// Optimized TournamentContext with intelligent real-time updates
import React, { createContext, useContext, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import {
  getTournaments,
  createTournament as dbCreateTournament,
  registerTeamForTournament,
  adminRegisterTeamForTournament,
  checkUserTournamentRegistration as dbCheckUserTournamentRegistration,
  removeTeamFromTournament,
  updateTournament,
  generateTournamentBracket,
  getTournamentMatches as dbGetTournamentMatches,
  getMatchDetails,
  updateMatchSchedule,
  updateJourneySchedule,
  recordMatchResult,
  updateMatchVodUrls as dbUpdateMatchVodUrls,
  submitTeamVodUrl as dbSubmitTeamVodUrl,
  getTournamentPlayerStats,
  getTournamentTeamStats,
  getTournamentLeaderboardStats,
  getTournamentMVPLeaderboard,
  getPlayerMVPStats,
  getMatchEligibleMVPPlayers,
  checkTournamentReadiness,
  getTournamentBracket,
  getMatchGames,
  getMatchWithGames,
  recordGameResult,
  createNextGameIfNeeded,
  getVersusStandings,
  getVersusWinners,
  getTournamentGroups,
  deleteTournament,
  submitTournamentMatchResult,
  approveTournamentSubmission,
  rejectTournamentSubmission,
  getPendingTournamentSubmissions,
  getTournamentMatchSubmissions,
  canSubmitTournamentResult,
  batchCheckTournamentSubmissionPermissions,
  verifyTournamentManagement,
  submitScheduleRequest as dbSubmitScheduleRequest,
  respondToScheduleRequest,
  getScheduleRequestsForUser,
  getScheduleNotifications,
  markScheduleNotificationAsRead,
  approveScheduleRequest,
  rejectScheduleRequest,
  cancelScheduleRequest,
  getActiveScheduleRequest,
  updateScheduleRequest,
  canUserManageScheduleRequest
} from '../services/database';
import { supabase } from '../lib/supabase';
import { useBaseContext, createBaseState } from './shared/useBaseContext';
import { enhancedSubscriptionManager } from './shared/enhancedSubscriptionManager';
import { createErrorHandler, handleAsyncOperation } from './shared/errorHandler';

const TournamentContext = createContext();

// Tournament-specific actions
const tournamentActions = {
  SET_TOURNAMENTS: 'SET_TOURNAMENTS',
  ADD_TOURNAMENT: 'ADD_TOURNAMENT',
  UPDATE_TOURNAMENT: 'UPDATE_TOURNAMENT',
  SET_MATCHES: 'SET_MATCHES',
  UPDATE_MATCH: 'UPDATE_MATCH',
  SET_TOURNAMENT_BRACKET: 'SET_TOURNAMENT_BRACKET',
  SET_VERSUS_STANDINGS: 'SET_VERSUS_STANDINGS',
  SET_TOURNAMENT_GROUPS: 'SET_TOURNAMENT_GROUPS',
  CLEAR_TOURNAMENT_DATA: 'CLEAR_TOURNAMENT_DATA'
};

// Enhanced tournament reducer with optimizations
const tournamentReducer = (state, action) => {
  switch (action.type) {
    case tournamentActions.SET_TOURNAMENTS:
      return { ...state, tournaments: action.payload, loading: false };
    
    case tournamentActions.ADD_TOURNAMENT:
      return {
        ...state,
        tournaments: [action.payload, ...state.tournaments]
      };
    
    case tournamentActions.UPDATE_TOURNAMENT:
      return {
        ...state,
        tournaments: state.tournaments.map(t =>
          t.id === action.payload.id ? { ...t, ...action.payload } : t
        )
      };
    
    case tournamentActions.SET_MATCHES:
      return { 
        ...state, 
        matches: { ...state.matches, [action.payload.tournamentId]: action.payload.matches }
      };
    
    case tournamentActions.UPDATE_MATCH:
      const tournamentId = action.payload.tournamentId;
      const updatedMatches = state.matches[tournamentId]?.map(match =>
        match.id === action.payload.match.id ? { ...match, ...action.payload.match } : match
      ) || [];
      
      return {
        ...state,
        matches: { ...state.matches, [tournamentId]: updatedMatches }
      };
    
    case tournamentActions.SET_TOURNAMENT_BRACKET:
      return {
        ...state,
        brackets: { ...state.brackets, [action.payload.tournamentId]: action.payload.bracket }
      };
    
    case tournamentActions.SET_VERSUS_STANDINGS:
      return {
        ...state,
        versusStandings: { ...state.versusStandings, [action.payload.tournamentId]: action.payload.standings }
      };
    
    case tournamentActions.SET_TOURNAMENT_GROUPS:
      return {
        ...state,
        tournamentGroups: { ...state.tournamentGroups, [action.payload.tournamentId]: action.payload.groups }
      };
    
    case tournamentActions.CLEAR_TOURNAMENT_DATA:
      const { [action.payload.tournamentId]: removedMatches, ...remainingMatches } = state.matches;
      const { [action.payload.tournamentId]: removedBracket, ...remainingBrackets } = state.brackets;
      const { [action.payload.tournamentId]: removedStandings, ...remainingStandings } = state.versusStandings;
      const { [action.payload.tournamentId]: removedGroups, ...remainingGroups } = state.tournamentGroups;
      
      return {
        ...state,
        matches: remainingMatches,
        brackets: remainingBrackets,
        versusStandings: remainingStandings,
        tournamentGroups: remainingGroups
      };
    
    default:
      return state;
  }
};

const initialState = createBaseState({
  tournaments: [],
  matches: {},
  brackets: {},
  versusStandings: {},
  tournamentGroups: {},
  realTimeStatus: {
    connected: false,
    subscriptions: {},
    lastUpdate: null
  }
});

export const TournamentProvider = ({ children }) => {
  const { state, dispatch, setLoading, setError, clearError } = useBaseContext(initialState, tournamentReducer);
  const { user } = useAuth();
  const errorHandler = useMemo(() => createErrorHandler('TOURNAMENT'), []);
  
  // Performance optimization refs
  const subscriptionsRef = useRef(new Set());
  const lastLoadTimes = useRef(new Map()); // Track last load times per tournament

  // Helper function to format tournament data
  const formatTournamentData = useCallback((tournament) => ({
    id: tournament.id,
    name: tournament.name,
    description: tournament.description,
    logo: tournament.logo, // Add the tournament logo field
    startDate: tournament.start_date,
    startTime: tournament.start_time,
    // New timezone-aware fields
    startDatetime: tournament.start_datetime,
    schedulerTimezone: tournament.scheduler_timezone,
    endDate: tournament.end_date,
    maxTeams: tournament.max_teams,
    minTeams: tournament.min_teams || 4,
    format: formatTournamentFormat(tournament.format),
    tournamentSizeFormat: tournament.tournament_size_format || '5v5',
    platform: tournament.platform || 'all',
    discord_link: tournament.discord_link || null,
    allowed_countries: tournament.allowed_countries,
    allowed_regions: tournament.allowed_regions,
    playoff_series_type: tournament.playoff_series_type,
    knockout_series_type: tournament.knockout_series_type,
    minutes_per_match: tournament.minutes_per_match,
    golden_goal_overtime: tournament.golden_goal_overtime,
    mercy_rule: tournament.mercy_rule,
    // Registration configuration
    invite_only: tournament.invite_only || false,
    // Journey system fields
    firstJourneyDate: tournament.first_journey_date,
    journeyDuration: tournament.journey_duration,
    totalJourneys: tournament.total_journeys,
    estimatedEndDate: tournament.estimated_end_date,
    registeredTeams: tournament.tournament_registrations?.map(reg => reg.team.id) || [],
    registeredTeamsDetails: tournament.tournament_registrations?.map(reg => ({
      id: reg.team.id,
      name: reg.team.name,
      logo: reg.team.logo || '🏆',
      description: reg.team.description,
      ownerId: reg.team.owner_id,
      ownerName: reg.team.owner?.display_name || reg.team.owner?.username,
      ownerAvatarUrl: reg.team.owner?.avatar_url,
      owner: reg.team.owner,
      country_code: reg.team.country_code,
      // Add tournament wins data
      tournament_wins: reg.team.tournament_wins || reg.team.trophies || 0,
      members: (reg.team.members || []).map(member => ({
        user_id: member.user_id,
        id: member.user?.id,
        username: member.user?.username,
        display_name: member.user?.display_name,
        avatar_url: member.user?.avatar_url,
        role: member.role,
        user: member.user
      }))
    })) || [],
    status: tournament.status,
    prize: tournament.prize_pool || '$0',
    createdBy: tournament.created_by,
    createdByUser: tournament.created_by?.username || 'Admin',
    createdAt: tournament.created_at,
    rules: tournament.rules || [],
    bracketGenerated: tournament.bracket_generated || false,
    bracketGeneratedAt: tournament.bracket_generated_at,
    official_streams: tournament.official_streams || [],
    section_id: tournament.section_id,
    winner: tournament.winner ? {
      id: tournament.winner.id,
      name: tournament.winner.name,
      logo: tournament.winner.logo || '🏆'
    } : null
  }), []);

  // Optimized tournament loading with rate limiting
  const loadTournaments = useCallback(async (isRealTimeUpdate = false) => {
    const now = Date.now();
    const lastLoad = lastLoadTimes.current.get('tournaments') || 0;
    
    // Rate limiting: prevent loads more frequent than every 30 seconds for real-time updates
    if (isRealTimeUpdate && now - lastLoad < 30000) {
      return;
    }
    
    return handleAsyncOperation(async () => {
      if (!isRealTimeUpdate) {
        setLoading(true);
      }
      clearError();
      lastLoadTimes.current.set('tournaments', now);
      
      const result = await getTournaments();
      if (result.success) {
        const transformedTournaments = result.data.map(formatTournamentData);
        
        // Only update if data has actually changed
        const hasChanged = JSON.stringify(state.tournaments) !== JSON.stringify(transformedTournaments);
        if (hasChanged) {
          dispatch({ type: tournamentActions.SET_TOURNAMENTS, payload: transformedTournaments });
          
          // Emit global update event only if data changed
          enhancedSubscriptionManager.emit('tournaments_updated', transformedTournaments);
        }
      } else {
        throw new Error(result.error);
      }
    }, errorHandler);
  }, [setLoading, clearError, dispatch, formatTournamentData, state.tournaments, errorHandler]);

  // Optimized match loading with intelligent batching
  const loadTournamentMatches = useCallback(async (tournamentId, isRealTimeUpdate = false) => {
    const now = Date.now();
    const loadKey = `matches_${tournamentId}`;
    const lastLoad = lastLoadTimes.current.get(loadKey) || 0;
    
    // Rate limiting for real-time updates (reduced for Swiss tournament advancement)
    if (isRealTimeUpdate && now - lastLoad < 5000) {
      return;
    }
    
    return handleAsyncOperation(async () => {
      lastLoadTimes.current.set(loadKey, now);
      
      const result = await dbGetTournamentMatches(tournamentId);
      
      if (result.success) {
        // Improved change detection for Swiss tournaments with complex nested structures
        const currentMatches = state.matches[tournamentId] || [];
        const newMatches = result.data || [];
        
        // Simple but effective change detection: length, status, and winners
        const hasChanged = 
          currentMatches.length !== newMatches.length ||
          newMatches.some((match, idx) => {
            const currentMatch = currentMatches[idx];
            return !currentMatch ||
                   match.status !== currentMatch.status ||
                   match.series_winner_id !== currentMatch.series_winner_id ||
                   match.round_number !== currentMatch.round_number ||
                   (match.games && currentMatch.games && match.games.length !== currentMatch.games.length);
          });
        
        if (hasChanged || isRealTimeUpdate) {
          dispatch({
            type: tournamentActions.SET_MATCHES,
            payload: { tournamentId, matches: result.data }
          });
          
          // Emit tournament-specific event
          enhancedSubscriptionManager.emit(`tournament_${tournamentId}_matches_updated`, result.data);
        }
      }
      return result;
    }, errorHandler);
  }, [dispatch, state.matches, errorHandler]);

  const loadTournamentBracket = useCallback(async (tournamentId, options = {}) => {
    return handleAsyncOperation(async () => {
      const result = await getTournamentBracket(tournamentId, options);
      if (result.success) {
        dispatch({
          type: tournamentActions.SET_TOURNAMENT_BRACKET,
          payload: { tournamentId, bracket: result.data }
        });
        
        // Emit bracket update event
        enhancedSubscriptionManager.emit(`tournament_${tournamentId}_bracket_updated`, result.data);
      }
      return result;
    }, errorHandler);
  }, [dispatch, errorHandler]);

  const loadVersusStandings = useCallback(async (tournamentId, isRealTimeUpdate = false) => {
    const now = Date.now();
    const loadKey = `standings_${tournamentId}`;
    const lastLoad = lastLoadTimes.current.get(loadKey) || 0;
    
    // Rate limiting for standings (reduced for Swiss tournament advancement)
    if (isRealTimeUpdate && now - lastLoad < 10000) {
      return;
    }
    
    return handleAsyncOperation(async () => {
      lastLoadTimes.current.set(loadKey, now);
      
      const result = await getVersusStandings(tournamentId);
      if (result.success) {
        // Only update if data has changed
        const currentStandings = state.versusStandings[tournamentId] || [];
        const hasChanged = JSON.stringify(currentStandings) !== JSON.stringify(result.data);
        
        if (hasChanged) {
          dispatch({
            type: tournamentActions.SET_VERSUS_STANDINGS,
            payload: { tournamentId, standings: result.data }
          });
          
          // Emit standings update event
          enhancedSubscriptionManager.emit(`tournament_${tournamentId}_standings_updated`, result.data);
        }
      }
      return result;
    }, errorHandler);
  }, [dispatch, state.versusStandings, errorHandler]);

  const loadTournamentGroups = useCallback(async (tournamentId) => {
    return handleAsyncOperation(async () => {
      const result = await getTournamentGroups(tournamentId);
      if (result.success) {
        dispatch({
          type: tournamentActions.SET_TOURNAMENT_GROUPS,
          payload: { tournamentId, groups: result.data }
        });
        
        // Emit groups update event
        enhancedSubscriptionManager.emit(`tournament_${tournamentId}_groups_updated`, result.data);
      }
      return result;
    }, errorHandler);
  }, [dispatch, errorHandler]);

  const handleSubmissionChange = useCallback(async (payload) => {
    // Tournament submission change detected
    
    // Emit tournament action update event for notifications (matches ScrimNotifications pattern)
    window.dispatchEvent(new CustomEvent('tournamentActionUpdate', { detail: payload }));
  }, []);

  // Set up optimized real-time subscriptions
  useEffect(() => {
    // Setting up real-time subscriptions
    const subscriptions = subscriptionsRef.current;
    
    // Initial data load
    loadTournaments();

    // Set up global tournament subscriptions with reduced debouncing for faster Swiss updates
    const debouncedSubmissionHandler = enhancedSubscriptionManager.debounce(handleSubmissionChange, 2000);

    const globalSubscription = enhancedSubscriptionManager.subscribeGlobalTournamentUpdates((payload) => {
      const { table, eventType } = payload;
      
      // Add additional filtering at the subscription level
      // Note: Most of these tables no longer have realtime subscriptions due to optimization
      switch (table) {
        case 'tournaments':
        case 'matches':
        case 'player_match_stats':
        case 'tournament_registrations':
        case 'team_group_assignments':
        case 'tournament_groups':
          // These tables no longer have realtime subscriptions - data updates via manual refresh
          break;
        case 'tournament_match_actions':
          // This table still has realtime enabled
          if (['INSERT', 'UPDATE'].includes(eventType)) {
            debouncedSubmissionHandler(payload);
          }
          break;
        default:
          // Unhandled table
          break;
      }
    });

    // Only add subscription if it's not null
    if (globalSubscription) {
      subscriptions.add(globalSubscription);
    }
    // Most subscriptions now use manual refresh for cost optimization

    return () => {
      // Cleaning up real-time subscriptions
      
      enhancedSubscriptionManager.unsubscribeAll();
      subscriptions.clear();
    };
  }, [loadTournaments, handleSubmissionChange]);

  // Helper functions remain the same
  const formatTournamentFormat = (dbFormat) => {
    const formatMap = {
      'single_elimination': 'Single Elimination',
      'round_robin': 'Round Robin',
      'bo3': 'Best of 3',
      'bo5': 'Best of 5',
      'league': 'League',
      'versus': 'Versus',
      'group_stage_knockout': 'Group Stage + Knockout'
    };
    return formatMap[dbFormat] || dbFormat;
  };

  // The rest of the functions remain largely the same but with optimized calls
  const createTournament = useCallback(async (tournamentData) => {
    return handleAsyncOperation(async () => {
      if (!user?.isAdmin) {
        throw new Error('Only administrators can create tournaments');
      }
      
      setLoading(true);
      clearError();

      // Pass the data with camelCase properties as expected by the database service
      const result = await dbCreateTournament(tournamentData, user.id);
      if (result.success) {
        const newTournament = formatTournamentData({
          ...result.data,
          tournament_registrations: []
        });

        dispatch({ type: tournamentActions.ADD_TOURNAMENT, payload: newTournament });
        setLoading(false);
        
        // Emit tournament created event
        enhancedSubscriptionManager.emit('tournament_created', newTournament);
        
        return { success: true, tournament: newTournament };
      } else {
        throw new Error(result.error);
      }
    }, errorHandler);
  }, [user, setLoading, clearError, dispatch, formatTournamentData, errorHandler]);

  const registerTeam = useCallback(async (tournamentId, teamId) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in to register a team');

      const result = await registerTeamForTournament(tournamentId, teamId);
      if (result.success) {
        await loadTournaments();
        
        // Emit team registered event
        enhancedSubscriptionManager.emit('team_registered', { tournamentId, teamId });
        
        return { success: true };
      } else {
        let errorMessage = result.error;
        if (result.conflicts && result.conflicts.length > 0) {
          const conflictList = result.conflicts.map(conflict =>
            `• ${conflict.username} is already in "${conflict.conflictingTeam.name}"`
          ).join('\n');
          errorMessage = `Cannot register team due to member conflicts:\n${conflictList}\n\nEach player can only be in one team per tournament.`;
        }
        throw new Error(errorMessage);
      }
    }, { setLoading, setError, clearError }, errorHandler, 'registerTeam');
  }, [user, setLoading, setError, clearError, loadTournaments, errorHandler]);

  const adminRegisterTeam = useCallback(async (tournamentId, teamId) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in to perform admin actions');
      
      setLoading(true);
      clearError();

      const result = await adminRegisterTeamForTournament(tournamentId, teamId, user.id);
      if (result.success) {
        await loadTournaments();
        setLoading(false);
        
        // Emit team registered event
        enhancedSubscriptionManager.emit('team_registered', { tournamentId, teamId });
        
        return { 
          success: true, 
          teamName: result.teamName 
        };
      } else {
        let errorMessage = result.error;
        if (result.conflicts && result.conflicts.length > 0) {
          const conflictList = result.conflicts.map(conflict =>
            `• ${conflict.username} is already in "${conflict.conflictingTeam.name}"`
          ).join('\n');
          errorMessage = `Cannot register team due to member conflicts:\n${conflictList}\n\nEach player can only be in one team per tournament.`;
        }
        throw new Error(errorMessage);
      }
    });
  }, [user, setLoading, clearError, loadTournaments]);

  const startTournament = useCallback(async (tournamentId, config = {}) => {
    return handleAsyncOperation(async () => {
      setLoading(true);

      const readinessCheck = await checkTournamentReadiness(tournamentId);
      if (!readinessCheck.success || !readinessCheck.data.canStart) {
        throw new Error(readinessCheck.data?.error || 'Tournament cannot be started');
      }

      const bracketResult = await generateTournamentBracket(tournamentId, user.id, config);
      if (!bracketResult.success) {
        throw new Error(bracketResult.error);
      }

      // Reload all tournament data
      // For ERCL tournaments, preserve phase context if available
      const tournament = state.tournaments.find(t => t.id === tournamentId);
      let bracketPromise;
      if (tournament?.format === 'ercl') {
        const currentUrl = window.location.href;
        let erclPhase = null;
        if (currentUrl.includes('playoffs')) {
          erclPhase = 'playoffs';
        } else if (currentUrl.includes('knockout')) {
          erclPhase = 'knockout';
        }
        
        if (erclPhase) {
          bracketPromise = loadTournamentBracket(tournamentId, { erclPhase });
        } else {
          bracketPromise = loadTournamentBracket(tournamentId);
        }
      } else {
        bracketPromise = loadTournamentBracket(tournamentId);
      }
      
      await Promise.all([
        loadTournaments(),
        loadTournamentMatches(tournamentId),
        bracketPromise
      ]);

      // For versus tournaments, also load standings and groups
      const tournamentForVersusReload = state.tournaments.find(t => t.id === tournamentId);
      if (tournamentForVersusReload?.format === 'Versus') {
        await Promise.all([
          loadVersusStandings(tournamentId),
          loadTournamentGroups(tournamentId)
        ]);
      }

      setLoading(false);
      
      // Emit tournament started event
      enhancedSubscriptionManager.emit('tournament_started', { tournamentId, config });
      
      return { success: true };
    }, errorHandler);
  }, [user, setLoading, loadTournaments, loadTournamentMatches, loadTournamentBracket, loadVersusStandings, loadTournamentGroups, state.tournaments, errorHandler]);

  // Continue with the rest of the functions (getMatchById, submitMatchResult, etc.)
  // They remain largely the same but now use the optimized loading functions

  const getMatchById = useCallback(async (matchId) => {
    return handleAsyncOperation(async () => {
      const result = await getMatchDetails(matchId);
      return result;
    }, errorHandler);
  }, [errorHandler]);

  const scheduleMatch = useCallback(async (matchId, scheduleData) => {
    return handleAsyncOperation(async () => {
      const result = await updateMatchSchedule(matchId, scheduleData, user.id);
      if (result.success) {
        const match = result.data;
        if (match.tournament_id) {
          await loadTournamentMatches(match.tournament_id);
        }
      }
      return result;
    }, errorHandler);
  }, [user, loadTournamentMatches, errorHandler]);

  const scheduleJourney = useCallback(async (tournamentId, journeyNumber, scheduleData) => {
    return handleAsyncOperation(async () => {
      const result = await updateJourneySchedule(tournamentId, journeyNumber, scheduleData, user.id);
      if (result.success) {
        await loadTournamentMatches(tournamentId);
      }
      return result;
    }, errorHandler);
  }, [user, loadTournamentMatches, errorHandler]);

  const submitMatchResult = useCallback(async (matchId, resultData) => {
    return handleAsyncOperation(async () => {
      const result = await recordMatchResult(matchId, resultData, user.id);
      if (result.success) {
        await loadTournaments();
        const matchResult = await getMatchDetails(matchId);
        if (matchResult.success && matchResult.data.tournament_id) {
          await loadTournamentMatches(matchResult.data.tournament_id);
          
          // For versus tournaments, update standings
          const tournament = state.tournaments.find(t => t.id === matchResult.data.tournament_id);
          if (tournament?.format === 'Versus') {
            await loadVersusStandings(matchResult.data.tournament_id);
          }
        }
        
        // Emit match result event
        enhancedSubscriptionManager.emit('match_result_submitted', { matchId, resultData });
      }
      return result;
    }, errorHandler);
  }, [user, loadTournaments, loadTournamentMatches, loadVersusStandings, state.tournaments, errorHandler]);

  const updateMatchVodUrls = useCallback(async (matchId, vodData) => {
    return handleAsyncOperation(async () => {
      const result = await dbUpdateMatchVodUrls(matchId, vodData, user.id);
      if (result.success) {
        // Refresh the match data
        const matchResult = await getMatchDetails(matchId);
        if (matchResult.success && matchResult.data.tournament_id) {
          await loadTournamentMatches(matchResult.data.tournament_id);
        }
        
        // Emit VOD URLs updated event
        enhancedSubscriptionManager.emit('match_vod_urls_updated', { matchId, vodData });
      }
      return result;
    }, errorHandler);
  }, [user, loadTournamentMatches, errorHandler]);

  // Team-specific VOD submission function for players
  const submitTeamVodUrl = useCallback(async (matchId, vodUrl) => {
    return handleAsyncOperation(async () => {
      const result = await dbSubmitTeamVodUrl(matchId, vodUrl, user.id);
      if (result.success) {
        // Refresh the match data
        const matchResult = await getMatchDetails(matchId);
        if (matchResult.success && matchResult.data.tournament_id) {
          await loadTournamentMatches(matchResult.data.tournament_id);
        }
        
        // Emit VOD URL updated event
        enhancedSubscriptionManager.emit('team_vod_url_updated', { 
          matchId, 
          vodUrl, 
          teamId: result.teamId,
          isTeam1: result.isTeam1 
        });
      }
      return result;
    }, errorHandler);
  }, [user, loadTournamentMatches, errorHandler]);

  const checkUserTournamentRegistration = useCallback(async (tournamentId) => {
    if (!user) return { isRegistered: false };
    
    try {
      const result = await dbCheckUserTournamentRegistration(tournamentId, user.id);
      return result;
    } catch (error) {
      console.error('Error checking user registration:', error);
      return { isRegistered: false };
    }
  }, [user]);

  // Helper functions
  const getTournamentById = useCallback((id) => {
    return state.tournaments.find(t => t.id === parseInt(id) || t.id === id);
  }, [state.tournaments]);

  const getUpcomingTournaments = useCallback(() => {
    const now = new Date();
    
    return state.tournaments
      .filter(t => {
        if (t.status !== 'open') return false;
        
        try {
          let tournamentDateTime;
          
          // Always use start_time if available, regardless of what's in start_date
          if (t.startDate && t.startTime) {
            // Extract just the date part (YYYY-MM-DD) from startDate
            const datePart = t.startDate.split('T')[0];
            // Combine with the actual time
            tournamentDateTime = new Date(`${datePart}T${t.startTime}`);
          }
          // Fallback if no time specified
          else if (t.startDate) {
            tournamentDateTime = new Date(t.startDate);
          }
          else {
            return false;
          }
          
          // Check if date is valid
          if (isNaN(tournamentDateTime.getTime())) {
            console.warn('Invalid tournament date:', t.name, t.startDate, t.startTime);
            return false;
          }
          
          return tournamentDateTime > now;
        } catch (error) {
          console.error('Error parsing tournament date:', t.name, error);
          return false;
        }
      })
      .sort((a, b) => {
        try {
          // Always extract date and combine with time for sorting
          const datePartA = a.startDate.split('T')[0];
          const datePartB = b.startDate.split('T')[0];
          const dateA = new Date(`${datePartA}T${a.startTime || '00:00'}`);
          const dateB = new Date(`${datePartB}T${b.startTime || '00:00'}`);
          return dateA - dateB;
        } catch (error) {
          return 0;
        }
      });
  }, [state.tournaments]);

  const getActiveTournaments = useCallback(() => {
    const now = new Date();
    
    return state.tournaments.filter(t => {
      // Include tournaments that are in progress, running, generating brackets, group stage, knockout, or playoffs
      if (['in_progress', 'running', 'bracket_generation', 'group_stage', 'knockout', 'playoffs'].includes(t.status)) {
        return true;
      }
      
      // For 'open' tournaments, only include if they have already started
      if (t.status === 'open') {
        try {
          let tournamentDateTime;
          
          // Always use start_time if available, regardless of what's in start_date
          if (t.startDate && t.startTime) {
            // Extract just the date part (YYYY-MM-DD) from startDate
            const datePart = t.startDate.split('T')[0];
            // Combine with the actual time
            tournamentDateTime = new Date(`${datePart}T${t.startTime}`);
          }
          // Fallback if no time specified
          else if (t.startDate) {
            tournamentDateTime = new Date(t.startDate);
          }
          else {
            return false;
          }
          
          // Check if date is valid
          if (isNaN(tournamentDateTime.getTime())) {
            console.warn('Invalid tournament date for active check:', t.name, t.startDate, t.startTime);
            return false;
          }
          
          // Only include if tournament has started (current time >= start time)
          return tournamentDateTime <= now;
        } catch (error) {
          console.error('Error parsing tournament date for active check:', t.name, error);
          return false;
        }
      }
      
      return false;
    });
  }, [state.tournaments]);

  const getTournamentMatches = useCallback((tournamentId) => {
    const matches = state.matches[tournamentId] || [];
    return matches;
  }, [state.matches]);

  const getTournamentBracketData = useCallback((tournamentId) => {
    return state.brackets[tournamentId] || {};
  }, [state.brackets]);

  const refreshTournaments = useCallback(() => {
    loadTournaments();
  }, [loadTournaments]);

  // BO3/BO5 specific functions
  const getMatchGamesData = useCallback(async (matchId) => {
    return handleAsyncOperation(async () => {
      const result = await getMatchGames(matchId);
      return result;
    }, errorHandler);
  }, [errorHandler]);

  const getMatchWithGamesData = useCallback(async (matchId) => {
    return handleAsyncOperation(async () => {
      const result = await getMatchWithGames(matchId);
      return result;
    }, errorHandler);
  }, [errorHandler]);

  const submitGameResult = useCallback(async (gameId, gameData) => {
    return handleAsyncOperation(async () => {
      // SECURITY FIX: BO3/BO5 games should use tournament submission system, not direct match editing
      // Get game details to check if it's a tournament match
      const gameResult = await getMatchDetails(gameId);
      
      if (gameResult.success && gameResult.data?.tournament_id) {
        // This is a tournament match - use the proper submission system instead of direct editing
        const matchData = gameResult.data;
        
        // Determine user's team ID - check both state.userTeams and direct API call
        let userTeamId = state.userTeams?.find(team => 
          team.id === matchData.team1_id || team.id === matchData.team2_id
        )?.id;
        
        let isAdminSubmission = false;
        
        // If not found in state, try direct verification
        if (!userTeamId) {
          try {
            // Check which team the user belongs to
            const { data: teamMemberData, error: membershipError } = await supabase
              .from('team_members')
              .select('team_id')
              .eq('user_id', user.id)
              .in('team_id', [matchData.team1_id, matchData.team2_id])
              .single();
              
            if (!membershipError) {
              userTeamId = teamMemberData?.team_id;
            }
          } catch (membershipError) {
          }
        }
        
        // If still no team membership, check if user is tournament owner/admin
        if (!userTeamId) {
          try {
            const permissionResult = await verifyTournamentManagement(user.id, matchData.tournament_id);
            if (permissionResult.isAdmin || permissionResult.isCreator || permissionResult.isOwner) {
              isAdminSubmission = true;
              // For admin submissions, use the first team as default (will be overridden by actual submission data)
              userTeamId = matchData.team1_id;
            }
          } catch (permissionError) {
            console.error('❌ Tournament permission verification failed:', permissionError);
            throw new Error('You must be a member of one of the participating teams or have tournament management permissions');
          }
        }
        
        if (!userTeamId) {
          throw new Error('Unable to determine your team membership or tournament permissions for this match');
        }
        
        const submissionData = {
          matchData: {
            team1_score: gameData.team1_score,
            team2_score: gameData.team2_score,
            playerStats: gameData.playerStats || [],
            mvpPlayerId: gameData.mvpPlayerId,
            isDnf: gameData.isDnf,
            dnfWinnerTeam: gameData.dnfWinnerTeam,
            // Add game-specific data for BO3/BO5
            gameNumber: gameData.gameNumber,
            isGameSubmission: true,
            targetGameId: gameId,
            isAdminSubmission: isAdminSubmission
          },
          notes: isAdminSubmission 
            ? `BO3/BO5 Game ${gameData.gameNumber} submission (Admin/Owner)` 
            : `BO3/BO5 Game ${gameData.gameNumber} submission`
        };
        
        if (isAdminSubmission) {
          // Tournament owners/admins use direct recording, not submission system
          const result = await recordGameResult(gameId, gameData, user.id);
          if (result.success) {
            // Refresh tournament data after game result
            await loadTournaments();
            await loadTournamentMatches(matchData.tournament_id);
            
            // For versus tournaments, update standings
            const tournament = state.tournaments.find(t => t.id === matchData.tournament_id);
            if (tournament?.format === 'Versus') {
              await loadVersusStandings(matchData.tournament_id);
            }
            
            // Emit game result event for knockout brackets
            if (tournament && (tournament.format === 'group_stage_knockout' || tournament.format === 'Group Stage + Knockout')) {
              // Force bracket refresh for knockout tournaments
              enhancedSubscriptionManager.emit('knockout_bracket_update', { 
                tournamentId: matchData.tournament_id,
                matchId: gameId,
                gameData 
              });
            }
          }
          return result;
        } else {
          console.log('📤 Submitting tournament match result:', { gameId, userTeamId, submissionData });
          // Regular players use tournament submission system
          return await submitTournamentMatchResult(gameId, userTeamId, submissionData);
        }
      } else {
        // This is likely a scrim match - use direct recording (for backward compatibility)
        const result = await recordGameResult(gameId, gameData, user.id);
        if (result.success) {
          // Refresh tournament data after game result
          await loadTournaments();
          const gameResult = await getMatchDetails(gameId);
          if (gameResult.success && gameResult.data.tournament_id) {
            await loadTournamentMatches(gameResult.data.tournament_id);
            
            // For versus tournaments, update standings
            const tournament = state.tournaments.find(t => t.id === gameResult.data.tournament_id);
            if (tournament?.format === 'Versus') {
              await loadVersusStandings(gameResult.data.tournament_id);
            }
          }
          
          // Emit game result event for knockout brackets
          const tournament = state.tournaments.find(t => t.id === gameResult.data.tournament_id);
          if (tournament && (tournament.format === 'group_stage_knockout' || tournament.format === 'Group Stage + Knockout')) {
            // Force bracket refresh for knockout tournaments
            enhancedSubscriptionManager.emit('knockout_bracket_update', { 
              tournamentId: gameResult.data.tournament_id,
              matchId: gameId,
              gameData 
            });
          }
          
          // Emit general game result event
          enhancedSubscriptionManager.emit('game_result_submitted', { gameId, gameData });
        }
        return result;
      }
    }, errorHandler);
  }, [user, loadTournaments, loadTournamentMatches, loadVersusStandings, state.tournaments, state.userTeams, errorHandler]);

  const createNextGame = useCallback(async (matchId) => {
    return handleAsyncOperation(async () => {
      const result = await createNextGameIfNeeded(matchId, user.id);
      
      if (result.success) {
        // Emit game created event
        enhancedSubscriptionManager.emit('game_created', { matchId });
      }
      
      return result;
    }, errorHandler);
  }, [user, errorHandler]);

  // Versus-specific functions with caching
  const getVersusStandingsData = useCallback(async (tournamentId) => {
    // Check if we have cached data
    if (state.versusStandings[tournamentId]) {
      return { success: true, data: state.versusStandings[tournamentId] };
    }
    
    // Load fresh data
    return await loadVersusStandings(tournamentId);
  }, [state.versusStandings, loadVersusStandings]);

  const getVersusWinnersData = useCallback(async (tournamentId) => {
    return handleAsyncOperation(async () => {
      const result = await getVersusWinners(tournamentId);
      return result;
    }, errorHandler);
  }, [errorHandler]);

  const getTournamentGroupsData = useCallback(async (tournamentId) => {
    // Check if we have cached data
    if (state.tournamentGroups[tournamentId]) {
      return { success: true, data: state.tournamentGroups[tournamentId] };
    }
    
    // Load fresh data
    return await loadTournamentGroups(tournamentId);
  }, [state.tournamentGroups, loadTournamentGroups]);

  const deleteTournamentById = useCallback(async (tournamentId) => {
    return handleAsyncOperation(async () => {
      if (!user?.isAdmin) {
        throw new Error('Only administrators can delete tournaments');
      }
      
      setLoading(true);
      clearError();

      const result = await deleteTournament(tournamentId, user.id);
      if (result.success) {
        // Remove tournament from state
        const updatedTournaments = state.tournaments.filter(t => t.id !== tournamentId);
        dispatch({ type: tournamentActions.SET_TOURNAMENTS, payload: updatedTournaments });
        
        // Clear related data
        dispatch({ type: tournamentActions.CLEAR_TOURNAMENT_DATA, payload: { tournamentId } });
        
        setLoading(false);
        
        // Emit tournament deleted event
        enhancedSubscriptionManager.emit('tournament_deleted', { tournamentId, data: result.data });
        
        return { success: true, data: result.data };
      } else {
        throw new Error(result.error);
      }
    }, errorHandler);
  }, [user, setLoading, clearError, dispatch, state.tournaments, errorHandler]);

  const removeTeamFromTournamentById = useCallback(async (tournamentId, teamId) => {
    return handleAsyncOperation(async () => {
      setLoading(true);
      clearError();

      const result = await removeTeamFromTournament(tournamentId, teamId, user.id);
      if (result.success) {
        // Reload tournaments to update the registration list
        await loadTournaments();
        setLoading(false);
        
        // Emit team removed event
        enhancedSubscriptionManager.emit('team_removed_from_tournament', { 
          tournamentId, 
          teamId, 
          data: result.data 
        });
        
        return result;
      } else {
        setLoading(false);
        throw new Error(result.error);
      }
    }, errorHandler);
  }, [user, setLoading, clearError, loadTournaments, errorHandler]);

  const updateTournamentById = useCallback(async (tournamentId, updateData) => {
    return handleAsyncOperation(async () => {
      setLoading(true);
      clearError();

      const result = await updateTournament(tournamentId, updateData, user.id);
      if (result.success) {
        // Find the current tournament to preserve registration data
        const currentTournament = state.tournaments.find(t => t.id === tournamentId);
        
        // Update the tournament in state
        const updatedTournament = formatTournamentData({
          ...result.data,
          tournament_registrations: currentTournament?.registeredTeamsDetails?.map(team => ({
            team: {
              id: team.id,
              name: team.name,
              logo: team.logo,
              description: team.description,
              members: team.members
            }
          })) || []
        });

        dispatch({ 
          type: tournamentActions.UPDATE_TOURNAMENT, 
          payload: updatedTournament 
        });
        
        setLoading(false);
        
        // Emit tournament updated event
        enhancedSubscriptionManager.emit('tournament_updated', { 
          tournamentId, 
          data: result.data 
        });
        
        return result;
      } else {
        setLoading(false);
        throw new Error(result.error);
      }
    }, errorHandler);
  }, [user, setLoading, clearError, dispatch, formatTournamentData, state.tournaments, errorHandler]);

  // Tournament submission functions
  const submitTournamentResult = useCallback(async (matchId, teamId, submissionData) => {
    return handleAsyncOperation(async () => {
      const result = await submitTournamentMatchResult(matchId, teamId, submissionData);
      
      if (result.success) {
        // Manual refresh after submission (replaces realtime updates)
        const matchResult = await getMatchDetails(matchId);
        if (matchResult.success && matchResult.data.tournament_id) {
          await Promise.all([
            loadTournamentMatches(matchResult.data.tournament_id),
            loadTournamentBracket(matchResult.data.tournament_id)
          ]);
        }
        
        // Emit tournament action update event for real-time notifications
        window.dispatchEvent(new CustomEvent('tournamentActionUpdate', { 
          detail: { matchId, teamId, action: 'submission_created', data: result.data } 
        }));
      }
      
      return result;
    }, errorHandler);
  }, [loadTournamentMatches, loadTournamentBracket, errorHandler]);

  const approveTournamentResult = useCallback(async (submissionId, selectedSubmissionId = null, reviewNotes = null) => {
    return handleAsyncOperation(async () => {
      const result = await approveTournamentSubmission(submissionId, selectedSubmissionId, reviewNotes);
      
      if (result.success) {
        // Emit tournament action update event for real-time notifications
        window.dispatchEvent(new CustomEvent('tournamentActionUpdate', { 
          detail: { 
            submissionId, 
            selectedSubmissionId,
            action: 'submission_approved', 
            data: result.data 
          } 
        }));
      }
      
      return result;
    }, errorHandler);
  }, [errorHandler]);

  const rejectTournamentResult = useCallback(async (submissionId, reviewNotes) => {
    return handleAsyncOperation(async () => {
      const result = await rejectTournamentSubmission(submissionId, reviewNotes);
      
      if (result.success) {
        // Emit tournament action update event for real-time notifications
        window.dispatchEvent(new CustomEvent('tournamentActionUpdate', { 
          detail: { 
            submissionId, 
            action: 'submission_rejected', 
            data: result.data 
          } 
        }));
      }
      
      return result;
    }, errorHandler);
  }, [errorHandler]);

  const loadPendingTournamentSubmissions = useCallback(async () => {
    if (!user) return { success: false, error: 'User not authenticated' };
    
    return handleAsyncOperation(async () => {
      return await getPendingTournamentSubmissions(user.id);
    }, errorHandler);
  }, [user, errorHandler]);

  const loadTournamentMatchSubmissions = useCallback(async (matchId) => {
    return handleAsyncOperation(async () => {
      return await getTournamentMatchSubmissions(matchId);
    }, errorHandler);
  }, [errorHandler]);

  const checkTournamentSubmissionPermissions = useCallback(async (matchId) => {
    if (!user) return { success: false, canSubmit: false, reason: 'User not authenticated' };
    
    return handleAsyncOperation(async () => {
      return await canSubmitTournamentResult(matchId, user.id);
    }, errorHandler);
  }, [user, errorHandler]);

  const batchCheckPermissions = useCallback(async (matchIds) => {
    if (!user) return { success: false, results: [] };
    
    return handleAsyncOperation(async () => {
      return await batchCheckTournamentSubmissionPermissions(matchIds, user.id);
    }, errorHandler);
  }, [user, errorHandler]);

  const getTournamentMedia = useCallback(async (tournamentId) => {
    return handleAsyncOperation(async () => {
      // For now, return empty data until the database table is created
      return { success: true, data: [] };
    }, errorHandler);
  }, [errorHandler]);

  // ========================================
  // 📅 MATCH SCHEDULING FUNCTIONS
  // ========================================

  const submitScheduleRequest = useCallback(async (matchId, proposerTeamId, scheduleData) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    
    return handleAsyncOperation(async () => {
      const result = await dbSubmitScheduleRequest(matchId, proposerTeamId, scheduleData);
      
      if (result.success) {
        // Emit schedule action update event for real-time notifications
        window.dispatchEvent(new CustomEvent('scheduleActionUpdate', { 
          detail: { matchId, action: 'schedule_request_created', data: result.data } 
        }));
      }
      
      return result;
    }, { setLoading, setError, clearError }, errorHandler, 'submitting schedule request');
  }, [user, setLoading, setError, clearError, errorHandler]);

  const respondToSchedule = useCallback(async (requestId, response, responseData = {}) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    
    return handleAsyncOperation(async () => {
      const result = await respondToScheduleRequest(requestId, user.id, response, responseData);
      
      if (result.success) {
        // Emit schedule action update event for real-time notifications
        window.dispatchEvent(new CustomEvent('scheduleActionUpdate', { 
          detail: { requestId, response, action: 'schedule_response', data: result.data } 
        }));
      }
      
      return result;
    }, errorHandler);
  }, [user, errorHandler]);

  const loadScheduleRequests = useCallback(async () => {
    if (!user) return { success: false, error: 'User not authenticated' };
    
    return handleAsyncOperation(async () => {
      return await getScheduleRequestsForUser(user.id);
    }, errorHandler);
  }, [user, errorHandler]);

  const loadScheduleNotifications = useCallback(async () => {
    if (!user) return { success: false, error: 'User not authenticated' };
    
    return handleAsyncOperation(async () => {
      return await getScheduleNotifications(user.id);
    }, errorHandler);
  }, [user, errorHandler]);

  const markScheduleNotificationRead = useCallback(async (notificationId) => {
    return handleAsyncOperation(async () => {
      return await markScheduleNotificationAsRead(notificationId);
    }, errorHandler);
  }, [errorHandler]);

  const approveSchedule = useCallback(async (requestId, adminNotes = null) => {
    return handleAsyncOperation(async () => {
      const result = await approveScheduleRequest(requestId, adminNotes);
      // Refresh data after approval
      await Promise.all([
        loadScheduleNotifications(),
        loadScheduleRequests()
      ]);
      return result;
    }, errorHandler);
  }, [loadScheduleNotifications, loadScheduleRequests, errorHandler]);

  const rejectSchedule = useCallback(async (requestId, adminNotes = null) => {
    return handleAsyncOperation(async () => {
      const result = await rejectScheduleRequest(requestId, adminNotes);
      // Refresh data after rejection
      await Promise.all([
        loadScheduleNotifications(),
        loadScheduleRequests()
      ]);
      return result;
    }, errorHandler);
  }, [loadScheduleNotifications, loadScheduleRequests, errorHandler]);

  const cancelSchedule = useCallback(async (requestId) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    
    return handleAsyncOperation(async () => {
      const result = await cancelScheduleRequest(requestId, user.id);
      
      if (result.success) {
        // Emit schedule action update event for real-time notifications
        window.dispatchEvent(new CustomEvent('scheduleActionUpdate', { 
          detail: { requestId, action: 'schedule_cancelled', data: result.data } 
        }));
      }
      
      return result;
    }, errorHandler);
  }, [user, errorHandler]);

  const checkActiveScheduleRequest = useCallback(async (matchId) => {
    return handleAsyncOperation(async () => {
      return await getActiveScheduleRequest(matchId);
    }, errorHandler);
  }, [errorHandler]);

  const updateExistingScheduleRequest = useCallback(async (requestId, proposerTeamId, scheduleData) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    
    return handleAsyncOperation(async () => {
      const result = await updateScheduleRequest(requestId, proposerTeamId, scheduleData);
      
      if (result.success) {
        // Emit schedule action update event for real-time notifications
        window.dispatchEvent(new CustomEvent('scheduleActionUpdate', { 
          detail: { requestId, action: 'schedule_request_updated', data: result.data } 
        }));
      }
      
      return result;
    }, { setLoading, setError, clearError }, errorHandler, 'updating schedule request');
  }, [user, setLoading, setError, clearError, errorHandler]);

  const checkScheduleRequestPermissions = useCallback(async (matchId) => {
    if (!user) return { success: false, error: 'User not authenticated' };
    
    return handleAsyncOperation(async () => {
      return await canUserManageScheduleRequest(matchId, user.id);
    }, errorHandler);
  }, [user, errorHandler]);

  // Manual refresh functions (replacing realtime subscriptions for optimization)
  const refreshTournamentData = useCallback(async (tournamentId) => {
    // Refresh all tournament-related data for a specific tournament
    await Promise.all([
      loadTournaments(),
      loadTournamentMatches(tournamentId),
      loadTournamentBracket(tournamentId)
    ]);
  }, [loadTournaments, loadTournamentMatches, loadTournamentBracket]);
  
  const refreshMatchData = useCallback(async (matchId) => {
    // Refresh match data and related tournament data
    const matchResult = await getMatchDetails(matchId);
    if (matchResult.success && matchResult.data.tournament_id) {
      await Promise.all([
        loadTournamentMatches(matchResult.data.tournament_id),
        loadTournamentBracket(matchResult.data.tournament_id)
      ]);
    }
  }, [loadTournamentMatches, loadTournamentBracket]);

  // Memoize real-time status to prevent unnecessary re-renders
  const subscriptionCount = subscriptionsRef.current.size;
  const realTimeStatus = useMemo(() => ({
    connected: subscriptionCount > 0,
    subscriptions: enhancedSubscriptionManager.getStatus()
    // Removed lastUpdate as it was causing constant re-renders
  }), [subscriptionCount]);

  const value = useMemo(() => ({
    ...state,
    // Tournament management
    createTournament,
    registerTeam,
    adminRegisterTeam,
    startTournament,
    deleteTournamentById,
    removeTeamFromTournamentById,
    updateTournamentById,
    
    // Data getters
    getTournamentById,
    getUpcomingTournaments,
    getActiveTournaments,
    getTournamentMatches,
    getTournamentBracketData,
    getTournamentPlayerStats,
    getTournamentTeamStats,
    getTournamentLeaderboardStats,
    
    // Data loaders
    loadTournamentMatches,
    loadTournamentBracket,
    loadVersusStandings,
    loadTournamentGroups,
    
    // Match functions
    getMatchById,
    scheduleMatch,
    scheduleJourney,
    submitMatchResult,
    updateMatchVodUrls,
    submitTeamVodUrl,
    
    // Tournament submission functions
    submitTournamentResult,
    approveTournamentResult,
    rejectTournamentResult,
    loadPendingTournamentSubmissions,
    loadTournamentMatchSubmissions,
    checkTournamentSubmissionPermissions,
    batchCheckPermissions,
    getTournamentMedia,
    
    // Match scheduling functions
    submitScheduleRequest,
    respondToSchedule,
    loadScheduleRequests,
    loadScheduleNotifications,
    markScheduleNotificationRead,
    approveSchedule,
    rejectSchedule,
    cancelSchedule,
    checkActiveScheduleRequest,
    updateExistingScheduleRequest,
    checkScheduleRequestPermissions,
    
    // BO3/BO5 functions
    getMatchGamesData,
    getMatchWithGamesData,
    submitGameResult,
    createNextGame,
    
    // Simplified MVP functions
    getTournamentMVPLeaderboard: async (tournamentId) => {
      return handleAsyncOperation(async () => {
        return await getTournamentMVPLeaderboard(tournamentId);
      }, errorHandler);
    },
    
    getPlayerMVPStats: async (playerId) => {
      return handleAsyncOperation(async () => {
        return await getPlayerMVPStats(playerId);
      }, errorHandler);
    },
    
    getMatchEligibleMVPPlayers: async (matchId) => {
      return handleAsyncOperation(async () => {
        return await getMatchEligibleMVPPlayers(matchId);
      }, errorHandler);
    },
    
    // Versus functions
    getVersusStandingsData,
    getVersusWinnersData,
    getTournamentGroupsData,
    
    // Utilities
    clearError,
    checkUserTournamentRegistration,
    refreshTournaments,
    
    // Manual refresh functions (replacing realtime subscriptions for optimization)
    refreshTournamentData,
    refreshMatchData,
    
    // Real-time status
    realTimeStatus
  }), [
    state,
    createTournament,
    registerTeam,
    adminRegisterTeam,
    startTournament,
    deleteTournamentById,
    removeTeamFromTournamentById,
    updateTournamentById,
    getTournamentById,
    getUpcomingTournaments,
    getActiveTournaments,
    getTournamentMatches,
    getTournamentBracketData,
    loadTournamentMatches,
    loadTournamentBracket,
    loadVersusStandings,
    loadTournamentGroups,
    getVersusStandingsData,
    getVersusWinnersData,
    getTournamentGroupsData,
    clearError,
    checkUserTournamentRegistration,
    refreshTournaments,
    submitTournamentResult,
    approveTournamentResult,
    rejectTournamentResult,
    loadPendingTournamentSubmissions,
    loadTournamentMatchSubmissions,
    checkTournamentSubmissionPermissions,
    batchCheckPermissions,
    getTournamentMedia,
    getMatchById,
    scheduleMatch,
    scheduleJourney,
    submitMatchResult,
    updateMatchVodUrls,
    submitTeamVodUrl,
    getMatchGamesData,
    getMatchWithGamesData,
    submitGameResult,
    createNextGame,
    errorHandler,
    realTimeStatus,
    refreshTournamentData,
    refreshMatchData,
    // Schedule functions
    submitScheduleRequest,
    respondToSchedule,
    loadScheduleRequests,
    loadScheduleNotifications,
    markScheduleNotificationRead,
    approveSchedule,
    rejectSchedule,
    cancelSchedule,
    checkActiveScheduleRequest,
    updateExistingScheduleRequest,
    checkScheduleRequestPermissions
  ]);

  return (
    <TournamentContext.Provider value={value}>
      {children}
    </TournamentContext.Provider>
  );
};

export const useTournaments = () => {
  const context = useContext(TournamentContext);
  if (!context) throw new Error('useTournaments must be used within a TournamentProvider');
  return context;
};

export default TournamentContext;
